import { ColDef } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { FC, useMemo } from 'react';
import { IFinanceRoundDataModel } from '../../../data-models/finance-round.data-model';
import { IFundDataModel } from '../../../data-models/fund.data-model';
import { TransactionType } from '../../../data-models/transaction-type.data-model';
import { ITransactionDataModel } from '../../../data-models/transaction.data-model';
import { fundsByIdMapAtom, transactionTypesByIdMapAtom } from '../../../services/state/AppConfigStateJ';
import { AgTable } from '../../../components/AgTable/AgTable';
import { AgFormatterService } from '../../../util/ag-formatter-service';
import { CompanyTransactionsState } from '../../CompanyProfiles/state/CompanyFundingsState';
import { CollapsibleSectionCard } from './CollapsibleSectionCard';
import {
  gridAutoCss,
  gridHeaderEllipsisCss,
  gridHugRowsCss,
  gridOverlayMutedCss,
} from './deals-detail.styles';

export interface IRoundTransactionRow {
  date: string | null;
  transactionType: string | null;
  security: string | null;
  shares: number | null;
  amountInvested: number | null;
  proceeds: number | null;
  fund: string | null;
}

/**
 * Select the company's transactions for a finance round and map them to rows:
 * date, transaction type, security, shares (negative for sells), invested amount,
 * proceeds (cash received on a sell) and fund. A transaction's `investmentRoundId`
 * is the finance round's `id` (see TransactionFormStep.preparePayload), so we
 * filter on `investmentRoundId === round.id`.
 *
 * Shares use the backend-normalized signed count `noOfSharesNormalized` (sells
 * negative, etc.) — the same field Finance History renders — falling back to the
 * raw `noOfShares` when normalization is absent.
 *
 * Scope: "my funds only" — restricted to transactions held by one of the org's own
 * funds (`fundId` present in `fundsById`, which is built from AppConfig funds). The
 * Security Overview, by contrast, shows every holder's securities for the round.
 */
export function selectTransactionRows(
  transactions: ITransactionDataModel[],
  roundId: number | null,
  fundsById: Map<number, IFundDataModel>,
  typesById: Map<number, TransactionType>
): IRoundTransactionRow[] {
  if (roundId == null) {
    return [];
  }
  return transactions
    .filter((t) => t.investmentRoundId === roundId && t.fundId != null && fundsById.has(t.fundId))
    .map((t) => ({
      date: t.transactionDate ?? null,
      transactionType: typesById.get(t.transactionTypeId)?.name ?? null,
      security: t.security ?? t.position ?? null,
      shares: t.noOfSharesNormalized ?? t.noOfShares ?? null,
      amountInvested: t.investmentAmount ?? null,
      proceeds: t.receivedAmount ?? null,
      fund: t.fundId != null ? (fundsById.get(t.fundId)?.name ?? null) : null,
    }));
}

const rightAligned = { textAlign: 'right' as const };

// Built at render time: AgFormatterService is not initialized at module load.
// Exported so tests can assert the column formatters without mounting a grid.
export function useTransactionColDefs(): ColDef<IRoundTransactionRow>[] {
  return useMemo(
    () => [
      {
        headerName: 'Date',
        field: 'date',
        flex: 1,
        minWidth: 110,
        headerTooltip: 'Date',
        valueFormatter: AgFormatterService.get().getFormatterForId('date'),
      },
      {
        headerName: 'Transaction Type',
        field: 'transactionType',
        flex: 1,
        minWidth: 140,
        headerTooltip: 'Transaction Type',
      },
      { headerName: 'Security', field: 'security', flex: 2, minWidth: 200, headerTooltip: 'Security' },
      {
        headerName: 'Shares',
        field: 'shares',
        flex: 1,
        minWidth: 100,
        cellStyle: rightAligned,
        headerTooltip: 'Shares',
        valueFormatter: AgFormatterService.get().getFormatterForId('integer'),
      },
      {
        headerName: 'Invested Amount',
        field: 'amountInvested',
        flex: 1,
        // Fits a full amount: flex bottoms out at minWidth in a narrow detail pane.
        minWidth: 160,
        cellStyle: rightAligned,
        headerTooltip: 'Invested Amount',
        // Exact to the cent, not abbreviated: these rows get reconciled against wires.
        valueFormatter: AgFormatterService.get().getFormatterForId('currency'),
      },
      {
        headerName: 'Proceeds',
        field: 'proceeds',
        flex: 1,
        minWidth: 160,
        cellStyle: rightAligned,
        headerTooltip: 'Proceeds',
        valueFormatter: AgFormatterService.get().getFormatterForId('currency'),
      },
      { headerName: 'Fund', field: 'fund', flex: 1, minWidth: 110, headerTooltip: 'Fund' },
    ],
    []
  );
}

interface RoundTransactionGridProps {
  round: IFinanceRoundDataModel;
  companyId: number;
}

/**
 * Transaction Overview (My funds only) section: the org's transactions for the
 * selected round, from `/transaction`. Suspends while transactions load.
 */
export const RoundTransactionGrid: FC<RoundTransactionGridProps> = ({ round, companyId }) => {
  const transactions = useAtomValue(CompanyTransactionsState(companyId));
  const fundsById = useAtomValue(fundsByIdMapAtom);
  const typesById = useAtomValue(transactionTypesByIdMapAtom);

  const rows = useMemo(
    () => selectTransactionRows(transactions, round.id, fundsById, typesById),
    [transactions, round.id, fundsById, typesById]
  );

  return <RoundTransactionGridView rows={rows} />;
};

/** Rendering half of the section, so a preview can show sample transactions without the store. */
export const RoundTransactionGridView: FC<{ rows: IRoundTransactionRow[] }> = ({ rows }) => {
  const columnDefs = useTransactionColDefs();

  return (
    <CollapsibleSectionCard
      title='Transaction Overview (My funds only)'
      ariaLabel='Transaction Overview'
      isEmpty={rows.length === 0}
      emptyAnnotation='No transactions'
    >
      <div css={[gridAutoCss, gridHeaderEllipsisCss, gridOverlayMutedCss, rows.length > 0 && gridHugRowsCss]}>
        <AgTable
          columnDefs={columnDefs}
          rowData={rows}
          domLayout='autoHeight'
          defaultColDef={{
            flex: 1,
            suppressSizeToFit: false,
            wrapHeaderText: false,
            autoHeaderHeight: false,
          }}
          rowGroupPanelShow='never'
          overlayNoRowsTemplate='No transactions for this round.'
        />
      </div>
    </CollapsibleSectionCard>
  );
};
